import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import supabase from '../supabase/supabase';
import { useAuth } from './AuthContext';

const DataContext = createContext();

export const useData = () => {
  const context = useContext(DataContext);
  if (!context) throw new Error('useData must be used within a DataProvider');
  return context;
}

export const DataProvider = ({ children }) => {
  const { user } = useAuth();
  const [contacts, setContacts] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [projects, setProjects] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [timeEntries, setTimeEntries] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [team, setTeam] = useState([]);
  const [customFields, setCustomFields] = useState([]);
  const [leadForms, setLeadForms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [contactTags, setContactTags] = useState(['Client', 'Lead', 'Prospect', 'Partner']);
  const [businessSettings, setBusinessSettings] = useState({
    companyName: 'JINXos Business',
    email: '',
    logo: '',
    fontFamily: 'Poppins',
    primaryColor: '#3B82F6',
    address: '',
    phone: ''
  });

  const fetchData = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const [
        { data: contactsData },
        { data: companiesData },
        { data: projectsData },
        { data: tasksData },
        { data: timeData },
        { data: proposalsData },
        { data: invoicesData },
        { data: teamData },
        { data: settingsData },
        { data: customFieldsData },
        { data: leadFormsData }
      ] = await Promise.all([
        supabase.from('contacts_1740000000000').select('*').eq('user_id', user.id).order('created_at', { ascending: false }),
        supabase.from('companies_1740000000000').select('*').eq('user_id', user.id).order('created_at', { ascending: false }),
        supabase.from('projects_1740000000000').select('*').eq('user_id', user.id).order('created_at', { ascending: false }),
        supabase.from('tasks_1740000000000').select('*').eq('user_id', user.id).order('created_at', { ascending: false }),
        supabase.from('time_entries_1740000000000').select('*').eq('user_id', user.id).order('date', { ascending: false }),
        supabase.from('proposals_1740000000000').select('*').eq('user_id', user.id).order('created_at', { ascending: false }),
        supabase.from('invoices_1740000000000').select('*').eq('user_id', user.id).order('created_at', { ascending: false }),
        supabase.from('team_members_1740000000000').select('*').eq('user_id', user.id).order('created_at', { ascending: false }),
        supabase.from('business_settings_1740000000000').select('*').eq('user_id', user.id).maybeSingle(),
        supabase.from('contact_custom_fields_1740000000000').select('*').eq('user_id', user.id).order('created_at', { ascending: true }),
        supabase.from('lead_forms_1740000000000').select('*').eq('user_id', user.id).order('created_at', { ascending: false })
      ]);

      setContacts((contactsData || []).map(c => ({ ...c, avatarUrl: c.avatar_url, companyId: c.company_id, customValues: c.custom_values || {} })));
      setCompanies((companiesData || []).map(c => ({ ...c, websiteUrl: c.website_url, logoUrl: c.logo_url, defaultHourlyRate: c.default_hourly_rate, ccFees: c.cc_fees, paymentTerms: c.payment_terms, leadSource: c.lead_source })));
      setProjects((projectsData || []).map(p => ({ ...p, companyId: p.company_id, contactId: p.contact_id, budgetHours: p.budget_hours, startDate: p.start_date, endDate: p.end_date, billingType: p.billing_type, isArchived: p.is_archived })));
      setTasks((tasksData || []).map(t => ({ ...t, projectId: t.project_id, projectName: t.project_name, dueDate: t.due_date, timeSpent: t.time_spent, estimatedTime: t.estimated_time, isTimerRunning: t.is_timer_running, timerSessions: t.timer_sessions || [], lastTimerStart: t.last_timer_start })));
      setProposals((proposalsData || []).map(p => ({ ...p, totalValue: p.total_value, designSettings: p.design_settings, availableAddOns: p.available_add_ons, clientInfo: p.client_info, signedAt: p.signed_at, sentAt: p.sent_at })));
      setInvoices((invoicesData || []).map(i => ({ ...i, taxAmount: i.tax_amount, dueDate: i.due_date, timeTracked: i.time_tracked, trackedTasks: i.tracked_tasks, sentAt: i.sent_at })));
      setCustomFields(customFieldsData || []);
      setLeadForms(leadFormsData || []);
      setTimeEntries((timeData || []).map(te => ({ ...te, projectId: te.project_id, taskId: te.task_id, teamMemberId: te.team_member_id, isBillable: te.is_billable, startTime: te.start_time, endTime: te.end_time })));
      setTeam(teamData || []);
      
      if (settingsData) {
        setBusinessSettings({
          companyName: settingsData.company_name || 'JINXos Business',
          logo: settingsData.logo || '',
          primaryColor: settingsData.primary_color || '#3B82F6',
          fontFamily: settingsData.font_family || 'Poppins',
          address: settingsData.address || '',
          phone: settingsData.phone || '',
          email: settingsData.email || ''
        });
      }
    } catch (err) {
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // CRITICAL: Robust Dynamic Payload Builder to prevent NULL overwrites
  const buildPayload = (data, mapping) => {
    const payload = {};
    Object.keys(mapping).forEach(uiKey => {
      const dbKey = mapping[uiKey];
      if (data[uiKey] !== undefined) {
        payload[dbKey] = data[uiKey];
      }
    });
    return payload;
  };

  const updateTimeEntry = async (id, data) => {
    const mapping = {
      description: 'description',
      projectId: 'project_id',
      taskId: 'task_id',
      teamMemberId: 'team_member_id',
      duration: 'duration',
      date: 'date',
      isBillable: 'is_billable',
      startTime: 'start_time',
      endTime: 'end_time'
    };
    const payload = buildPayload(data, mapping);
    const { error } = await supabase.from('time_entries_1740000000000').update(payload).eq('id', id);
    if (!error) await fetchData();
    return { error };
  };

  const addTimeEntry = async (data) => {
    const mapping = {
      description: 'description',
      projectId: 'project_id',
      taskId: 'task_id',
      teamMemberId: 'team_member_id',
      duration: 'duration',
      date: 'date',
      isBillable: 'is_billable',
      startTime: 'start_time',
      endTime: 'end_time'
    };
    const payload = { ...buildPayload(data, mapping), user_id: user.id };
    const { error } = await supabase.from('time_entries_1740000000000').insert([payload]);
    if (!error) await fetchData();
    return { error };
  };

  const updateTask = async (id, data) => {
    const mapping = {
      title: 'title',
      description: 'description',
      projectId: 'project_id',
      project_name: 'project_name',
      assignee: 'assignee',
      status: 'status',
      priority: 'priority',
      dueDate: 'due_date',
      timeSpent: 'time_spent',
      isTimerRunning: 'is_timer_running',
      timerSessions: 'timer_sessions',
      lastTimerStart: 'last_timer_start'
    };
    const payload = buildPayload(data, mapping);
    const { error } = await supabase.from('tasks_1740000000000').update(payload).eq('id', id);
    if (!error) await fetchData();
    return { error };
  };

  const updateProject = async (id, data) => {
    const mapping = {
      name: 'name',
      description: 'description',
      status: 'status',
      budget: 'budget',
      budgetHours: 'budget_hours',
      deadline: 'deadline',
      startDate: 'start_date',
      endDate: 'end_date',
      billingType: 'billing_type',
      companyId: 'company_id',
      contactId: 'contact_id',
      isArchived: 'is_archived'
    };
    const payload = buildPayload(data, mapping);
    const { error } = await supabase.from('projects_1740000000000').update(payload).eq('id', id);
    if (!error) await fetchData();
    return { error };
  };

  const value = {
    contacts, companies, projects, tasks, timeEntries, proposals, invoices, team, 
    customFields, leadForms, loading, businessSettings, contactTags, setContactTags,
    addTimeEntry, updateTimeEntry, deleteTimeEntry: (id) => supabase.from('time_entries_1740000000000').delete().eq('id', id).then(fetchData),
    addProject: (data) => supabase.from('projects_1740000000000').insert([{ ...buildPayload(data, { name: 'name', description: 'description', status: 'status', budget: 'budget', budgetHours: 'budget_hours', deadline: 'deadline', startDate: 'start_date', endDate: 'end_date', billingType: 'billing_type', companyId: 'company_id', contactId: 'contact_id' }), user_id: user.id }]).then(fetchData),
    updateProject,
    addTask: (data) => supabase.from('tasks_1740000000000').insert([{ ...buildPayload(data, { title: 'title', description: 'description', projectId: 'project_id', projectName: 'project_name', assignee: 'assignee', status: 'status', priority: 'priority', dueDate: 'due_date' }), user_id: user.id }]).then(fetchData),
    updateTask,
    deleteTask: (id) => supabase.from('tasks_1740000000000').delete().eq('id', id).then(fetchData),
    refresh: fetchData,
    updateBusinessSettings: (data) => supabase.from('business_settings_1740000000000').upsert({ ...buildPayload(data, { companyName: 'company_name', logo: 'logo', primaryColor: 'primary_color', fontFamily: 'font_family', address: 'address', phone: 'phone', email: 'email' }), user_id: user.id }).then(fetchData)
  };

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
};